import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Select } from '../components/ui/Forms';
import { Modal } from '../components/ui/Modal';
import SafeIcon from '../common/SafeIcon';
import SmtpPanel from './settings/SmtpPanel';
import DiagnosticPanel from './settings/DiagnosticPanel';
import PaymentMethodsPanel from './settings/PaymentMethodsPanel';
import { Tabs } from '../components/ui/Tabs';
import { motion, AnimatePresence } from 'framer-motion';
import { getNextCronExecution } from '../utils/dateUtils';
import { format } from 'date-fns';
import { SUPPORTED_CURRENCIES } from '../utils/currencyUtils';

const ICON_OPTIONS = ['Tv', 'Music', 'Monitor', 'Zap', 'Activity', 'Play', 'ShoppingBag', 'Coffee', 'Heart', 'Code', 'Tag'];

export default function Settings() {
  const { state, updateSettings, addCategory, updateCategory } = useAppContext();
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catFormData, setCatFormData] = useState({ name: '', icon: 'Tag' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const tabs = [
    { id: 'general', label: 'General', icon: <SafeIcon name="Settings" className="w-4 h-4" /> },
    { id: 'payment', label: 'Payment', icon: <SafeIcon name="CreditCard" className="w-4 h-4" /> },
    { id: 'currency', label: 'Currency', icon: <SafeIcon name="DollarSign" className="w-4 h-4" /> },
    { id: 'categories', label: 'Categories', icon: <SafeIcon name="Tag" className="w-4 h-4" /> },
    ...(profile?.role === 'admin' ? [{ id: 'delivery', label: 'Delivery & SMTP', icon: <SafeIcon name="Mail" className="w-4 h-4" /> }] : [])
  ];

  const handleFrequencyChange = async (freq) => {
    setIsUpdating(true);
    await updateSettings({ frequency: freq });
    setIsUpdating(false);
  };

  const handleCurrencyChange = async (curr) => {
    setIsUpdating(true);
    await updateSettings({ preferredCurrency: curr });
    setIsUpdating(false);
  };

  const handleOpenCatModal = (cat = null) => {
    if (cat) {
      setEditingCat(cat);
      setCatFormData({ name: cat.name, icon: cat.icon });
    } else {
      setEditingCat(null);
      setCatFormData({ name: '', icon: 'Tag' });
    }
    setIsCatModalOpen(true);
  };

  const cronInfo = getNextCronExecution(state.settings.frequency, profile?.last_report_sent);

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500 mt-1">Configure your workspace and system delivery parameters.</p>
        </div>
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} >
          {activeTab === 'general' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-teal-50 text-teal-600 rounded-2xl">
                      <SafeIcon name="Bell" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800">Notification Rules</h2>
                  </div>
                </div>
                <div className="space-y-8">
                  <Input label="Notification Email" value={state.settings.email} disabled />
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-3 block uppercase tracking-wider">Digest Frequency</label>
                    <div className="grid grid-cols-3 gap-3">
                      {['Daily', 'Weekly', 'Monthly'].map(freq => (
                        <button key={freq} disabled={isUpdating} onClick={() => handleFrequencyChange(freq)} className={`py-3.5 rounded-2xl border-2 text-sm font-bold transition-all relative ${state.settings.frequency === freq ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-sm' : 'border-slate-50 bg-slate-50/50 text-slate-400 hover:border-slate-200'}`} >
                          {freq}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-slate-900 p-8 rounded-[32px] text-white shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full -mr-16 -mt-16 blur-3xl" />
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-teal-500/20 rounded-lg">
                      <SafeIcon name="Clock" className="text-teal-400" />
                    </div>
                    <h3 className="font-bold tracking-tight">Next Delivery Window</h3>
                  </div>
                  <div className="space-y-1">
                    <p className="text-4xl font-black text-white tabular-nums tracking-tighter">
                      {cronInfo.relative}
                    </p>
                    <p className="text-slate-400 text-sm font-medium">{cronInfo.localTime}</p>
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 text-xs font-bold text-teal-400 uppercase tracking-widest">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                      System Status: Active
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-6 rounded-3xl border border-amber-100">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <SafeIcon name="Zap" className="w-4 h-4" /> Optimization Advice
                  </h4>
                  <p className="text-amber-700 text-xs leading-relaxed font-medium">
                    Our Toucan engine processes your digest every morning at 08:00 UTC. To ensure all your latest receipts are included, log them at least 1 hour before the delivery window.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <PaymentMethodsPanel />
          )}

          {activeTab === 'currency' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl">
                      <SafeIcon name="DollarSign" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">Local Currency</h2>
                      <p className="text-xs text-slate-500 font-medium">Set your default display currency</p>
                    </div>
                  </div>
                  <Select 
                    label="Preferred Currency" 
                    value={state.settings.preferredCurrency} 
                    onChange={e => handleCurrencyChange(e.target.value)}
                    options={SUPPORTED_CURRENCIES.map(c => ({ label: `${c.name} (${c.code})`, value: c.code }))}
                  />
                  <p className="mt-4 text-xs text-slate-500 italic leading-relaxed">
                    All subscription costs will be automatically converted to this currency using real-time market rates for analytics and totals.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-teal-50 text-teal-600 rounded-2xl">
                        <SafeIcon name="TrendingUp" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-slate-800">Live Exchange Rates</h2>
                        <p className="text-xs text-slate-500 font-medium">Market rates relative to 1 USD</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Last Synced</p>
                      <p className="text-xs font-mono font-bold text-teal-600">{format(new Date(), 'HH:mm')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {SUPPORTED_CURRENCIES.filter(c => c.code !== 'USD').map(curr => (
                      <div key={curr.code} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col items-center">
                        <span className="text-[10px] font-bold text-slate-400 mb-1">{curr.code}</span>
                        <span className="text-lg font-bold text-slate-700">{state.exchangeRates[curr.code]?.toFixed(4) || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl">
                    <SafeIcon name="Tag" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800">Expense Categories</h2>
                </div>
                <Button onClick={() => handleOpenCatModal()} className="rounded-xl px-6">
                  <SafeIcon name="Plus" /> New Category
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {state.categories.map(cat => (
                  <div key={cat.id} className="group flex items-center justify-between p-4 bg-slate-50 hover:bg-white hover:shadow-md hover:border-teal-200 rounded-2xl border border-slate-50 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white rounded-xl shadow-sm group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                        <SafeIcon name={cat.icon} className="w-5 h-5" />
                      </div>
                      <span className="font-bold text-slate-700">{cat.name}</span>
                    </div>
                    <button onClick={() => handleOpenCatModal(cat)} className="p-2 text-slate-300 hover:text-teal-600 transition-colors">
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'delivery' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <SmtpPanel profile={profile} />
              </div>
              <div className="lg:col-span-7">
                <DiagnosticPanel profile={profile} />
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <Modal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} title={editingCat ? "Edit Category" : "New Category"}>
        <form onSubmit={(e) => { e.preventDefault(); editingCat ? updateCategory(editingCat.id, catFormData) : addCategory(catFormData); setIsCatModalOpen(false); }} className="space-y-8">
          <Input label="Category Name" value={catFormData.name} onChange={e => setCatFormData({ ...catFormData, name: e.target.value })} required placeholder="e.g. Entertainment" />
          <div>
            <label className="text-sm font-bold text-slate-700 mb-4 block uppercase tracking-wider">Select Icon</label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
              {ICON_OPTIONS.map(i => (
                <button key={i} type="button" onClick={() => setCatFormData({ ...catFormData, icon: i })} className={`p-4 rounded-2xl flex items-center justify-center transition-all ${catFormData.icon === i ? 'bg-teal-600 text-white shadow-lg shadow-teal-200 scale-110' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`} >
                  <SafeIcon name={i} className="w-5 h-5" />
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
            <Button type="button" variant="secondary" onClick={() => setIsCatModalOpen(false)}>Cancel</Button>
            <Button type="submit" className="px-8">Save Category</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}